import apiClient from '../api/client';
import { ECPT_ENDPOINTS } from '../api/endpoints';
import type {
  GetCaseViewsParams,
  GetCaseViewsResponse,
  GetCaseDataParams,
  GetCaseDataResponse,
  GetCaseDataV2Params,
  GetCaseDataV2Response,
  PatchPemStatusBody,
  PatchPemStatusResponse,
  PatchPemCompletionStatusBody,
  PatchPemCompletionStatusResponse,
} from '../../types/api';

const ecptGetCaseService = {
  /**
   * GET /get-case/views
   * Lightweight view list for the dropdown.
   */
  getViews: async (
    params: GetCaseViewsParams,
  ): Promise<GetCaseViewsResponse> => {
    const { data } = await apiClient.get<GetCaseViewsResponse>(
      ECPT_ENDPOINTS.GET_CASE_VIEWS,
      { params },
    );
    return data;
  },

  /**
   * GET /get-case/data
   * pem_info rows filtered to get_case = true, using the selected view.
   */
  getData: async (
    params: GetCaseDataParams,
  ): Promise<GetCaseDataResponse> => {
    const { filters, search_text, ...rest } = params;
    const searchParams = new URLSearchParams(
      Object.fromEntries(Object.entries(rest).filter(([, v]) => v != null).map(([k, v]) => [k, String(v)]))
    );
    const searchText = search_text?.trim();
    if (searchText) searchParams.set('search_text', searchText);
    if (filters) {
      Object.entries(filters)
        .filter(([, v]) => v.trim() !== '')
        .forEach(([key, value]) => searchParams.append('filters', `${key}:${value.trim()}`));
    }
    const { data } = await apiClient.get<GetCaseDataResponse>(
      `${ECPT_ENDPOINTS.GET_CASE_DATA}?${searchParams}`,
    );
    return data;
  },

  /**
   * GET /get-case/data/v2
   * Server-side grouped data (get_case=true only) with PEM-based pagination.
   */
  getDataV2: async (
    params: GetCaseDataV2Params,
  ): Promise<GetCaseDataV2Response> => {
    const { filters, search_text, ...rest } = params;
    const searchParams = new URLSearchParams(
      Object.fromEntries(Object.entries(rest).filter(([, v]) => v != null).map(([k, v]) => [k, String(v)]))
    );
    const searchText = search_text?.trim();
    if (searchText) searchParams.set('search_text', searchText);
    if (filters) {
      Object.entries(filters)
        .filter(([, v]) => v.trim() !== '')
        .forEach(([key, value]) => searchParams.append('filters', `${key}:${value.trim()}`));
    }
    const { data } = await apiClient.get<GetCaseDataV2Response>(
      `${ECPT_ENDPOINTS.GET_CASE_DATA_V2}?${searchParams}`,
    );
    return data;
  },

  /** PATCH /get-case/pem-status */
  updatePemStatus: async (body: PatchPemStatusBody): Promise<PatchPemStatusResponse> => {
    const { data } = await apiClient.patch<PatchPemStatusResponse>(
      ECPT_ENDPOINTS.GET_CASE_PEM_STATUS,
      body,
    );
    return data;
  },

  /** PATCH /get-case/pem-completion-status */
  updatePemCompletionStatus: async (
    body: PatchPemCompletionStatusBody,
  ): Promise<PatchPemCompletionStatusResponse> => {
    const { data } = await apiClient.patch<PatchPemCompletionStatusResponse>(
      ECPT_ENDPOINTS.GET_CASE_PEM_COMPLETION_STATUS,
      body,
    );
    return data;
  },
};

export default ecptGetCaseService;
